import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrganizationContextDocument,
  createOrganizationContextReplacement,
  getCanonicalLibraryFiles,
  getOrganizationContextDocuments,
  requestOrganizationContextDeletion,
  setOrganizationContextInclusion,
  type OrganizationContextDocument,
  type OrganizationContextScope,
} from "@/services/organizationContextService";

// HEY-212: while any version is queued/extracting/privacy_review/
// indexing the documents poll continues on a short interval, so a
// leader flipping the switch on sees pending become green "Used in
// Chat" without a manual reload.
const IN_FLIGHT_STATES = ["queued", "extracting", "privacy_review", "indexing"];
const POLL_INTERVAL_MS = 500;

function hasInFlightVersion(documents: OrganizationContextDocument[] | undefined): boolean {
  return Boolean(documents?.some((document) =>
    document.versions.some((version) => IN_FLIGHT_STATES.includes(version.lifecycleState)),
  ));
}

export function useOrganizationContext(scope: OrganizationContextScope, schoolId: string | null, enabled = true) {
  const queryClient = useQueryClient();
  const queryKey = ["organization-context", scope, schoolId] as const;
  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  return {
    documents: useQuery({
      queryKey,
      queryFn: () => getOrganizationContextDocuments(scope, schoolId),
      enabled,
      staleTime: 30_000,
      refetchInterval: (query) => (hasInFlightVersion(query.state.data) ? POLL_INTERVAL_MS : false),
    }),
    libraryFiles: useQuery({
      queryKey: ["organization-context", "library-files"],
      queryFn: getCanonicalLibraryFiles,
      enabled,
      staleTime: 30_000,
    }),
    create: useMutation({ mutationFn: createOrganizationContextDocument, onSuccess: invalidate }),
    replace: useMutation({ mutationFn: createOrganizationContextReplacement, onSuccess: invalidate }),
    remove: useMutation({ mutationFn: requestOrganizationContextDeletion, onSuccess: invalidate }),
    updateInclusion: useMutation({ mutationFn: setOrganizationContextInclusion, onSuccess: invalidate }),
  };
}
